import './style.css';
import { subscribe, getState, showToast } from './store.js';
import {
  goLock, ensureDefaultKey, ensureHomeAddresses, setOnline, checkWebauthnAvailable,
  cancelOpenAccount, cancelAddRecipient, cancelImportKey, cancelConfirmReveal, closeStatusPanel,
  closeProfileMenu, closeLicense
} from './actions.js';
import { render } from './app.js';
import { initSwUpdateWatcher } from './sw-update.js';

const root = document.getElementById('app');

subscribe(() => render(root));
render(root);

// ---- Modal focus trap + Escape-to-close --------------------------------
// A single delegated listener, added once, rather than per-modal-render —
// the app fully rebuilds the DOM on every state change, so anything bound
// per-render would need explicit cleanup to avoid leaking duplicates.
function openModalCancel(state) {
  if (state.showOpenAccount) return cancelOpenAccount;
  if (state.showAddRecipient) return cancelAddRecipient;
  if (state.showImportKey) return cancelImportKey;
  if (state.showConfirmReveal) return cancelConfirmReveal;
  if (state.showStatusPanel) return closeStatusPanel;
  if (state.showProfileMenu) return closeProfileMenu;
  if (state.showLicense) return closeLicense;
  return null;
}

document.addEventListener('keydown', (e) => {
  const cancel = openModalCancel(getState());
  if (!cancel) return;

  if (e.key === 'Escape') {
    e.preventDefault();
    cancel();
    return;
  }

  if (e.key === 'Tab') {
    const modalCard = document.querySelector('.modal-card');
    if (!modalCard) return;
    const focusables = modalCard.querySelectorAll('button, input, select, textarea, a[href]');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
});

ensureDefaultKey();
ensureHomeAddresses();
checkWebauthnAvailable();

window.addEventListener('online', () => setOnline(true));
window.addEventListener('offline', () => setOnline(false));

initSwUpdateWatcher();

// Auto-lock after inactivity and clear sensitive in-memory state (CLAUDE.md),
// with a warning before it fires so it doesn't cut off an in-progress flow.
const AUTO_LOCK_MS = 5 * 60 * 1000;
const LOCK_WARNING_MS = 30 * 1000;
let lockTimer = null;
let warningTimer = null;
// Showing the warning toast is itself a state change, which would
// otherwise re-trigger resetLockTimer via the subscribe below and reset
// the very timer it's warning about. Suppress that one reentrant call.
let suppressReset = false;

function armTimers() {
  clearTimeout(lockTimer);
  clearTimeout(warningTimer);
  const s = getState();
  if (s.screen === 'landing') return;

  warningTimer = setTimeout(() => {
    const cur = getState();
    if (cur.screen === 'landing') return;
    suppressReset = true;
    showToast('Locking in 30s — tap anywhere to stay unlocked');
    suppressReset = false;
  }, AUTO_LOCK_MS - LOCK_WARNING_MS);

  lockTimer = setTimeout(() => {
    const cur = getState();
    if (cur.screen !== 'landing') goLock();
  }, AUTO_LOCK_MS);
}

function resetLockTimer() {
  if (suppressReset) return;
  armTimers();
}

['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
  document.addEventListener(evt, resetLockTimer, { passive: true })
);
subscribe(resetLockTimer);
resetLockTimer();
